// ─── Types ───────────────────────────────────────────────────────────────────
// ─── Constants ───────────────────────────────────────────────────────────────
export const SHORT_MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
// ─── ISO Week Utilities ──────────────────────────────────────────────────────
export function getISOWeek(d) {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}
export function getISOWeeksInYear(year) {
    const dec28 = new Date(year, 11, 28);
    const dow = dec28.getDay() || 7;
    const thu = new Date(dec28);
    thu.setDate(dec28.getDate() - dow + 4);
    const jan1 = new Date(thu.getFullYear(), 0, 1);
    return 1 + Math.round(((thu.getTime() - jan1.getTime()) / 86400000 - 3 + ((jan1.getDay() + 6) % 7)) / 7);
}
export function getCurrentISOWeek() {
    return getISOWeek(new Date());
}
export function getYearWeekFromDate(d) {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const year = date.getFullYear();
    const week1 = new Date(year, 0, 4);
    const week = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    return { week, year };
}
export function getISOWeekStartDate(year, week) {
    const jan4 = new Date(year, 0, 4);
    const dow = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
    return monday;
}
export function getISOWeekRange(year, week) {
    const monday = getISOWeekStartDate(year, week);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: formatISODate(monday), end: formatISODate(sunday) };
}
// ─── YearWeek Navigation ─────────────────────────────────────────────────────
export function prevYearWeek(yw) {
    if (yw.week <= 1) {
        return { week: getISOWeeksInYear(yw.year - 1), year: yw.year - 1 };
    }
    return { week: yw.week - 1, year: yw.year };
}
export function nextYearWeek(yw) {
    const maxWeeks = getISOWeeksInYear(yw.year);
    if (yw.week >= maxWeeks) {
        return { week: 1, year: yw.year + 1 };
    }
    return { week: yw.week + 1, year: yw.year };
}
export function isYearWeekBefore(a, b) {
    return a.year < b.year || (a.year === b.year && a.week < b.week);
}
export function isYearWeekAfter(a, b) {
    return a.year > b.year || (a.year === b.year && a.week > b.week);
}
// ─── Date Formatting ─────────────────────────────────────────────────────────
/** Format a Date to "YYYY-MM-DD" */
export function formatISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
/** Format a Date to "DD-MM-YYYY" for display */
export function formatDisplayDate(d) {
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}
/** Format a Date to "1 Jan 2026" */
export function formatShortDate(d) {
    return `${d.getDate()} ${SHORT_MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}
/**
 * Parse an ISO date string (e.g. "2026-01-19T00:00:00") into parts without
 * going through `new Date()`, avoiding timezone shifts.
 */
export function parseISODateParts(iso) {
    const parts = iso.split("T")[0].split("-");
    return {
        day: Number.parseInt(parts[2], 10),
        month: Number.parseInt(parts[1], 10) - 1,
        year: parts[0],
    };
}
/** Format an ISO string to "1 Jan 2026" using string parsing (no timezone shift) */
export function formatISOToShortDate(iso) {
    const { day, month, year } = parseISODateParts(iso);
    return `${day} ${SHORT_MONTH_NAMES[month]} ${year}`;
}
/** Format an ISO string to "1 Jan" (no year) using string parsing */
export function formatISOToShortDateNoYear(iso) {
    const { day, month } = parseISODateParts(iso);
    return `${day} ${SHORT_MONTH_NAMES[month]}`;
}
/** Extract "YYYY-MM-DD" from an ISO string without going through `new Date()` */
export function extractDateFromISO(iso) {
    return iso.split("T")[0];
}
// ─── Date Range Builders ─────────────────────────────────────────────────────
export function clampDate(dateStr, minStr, maxStr) {
    if (dateStr < minStr)
        return minStr;
    if (dateStr > maxStr)
        return maxStr;
    return dateStr;
}
export function buildWeekOptions(startDate, endDate) {
    const result = [];
    let cur = getYearWeekFromDate(new Date(startDate));
    const endYW = getYearWeekFromDate(new Date(endDate));
    const sd = new Date(startDate);
    const weekStart = getISOWeekStartDate(cur.year, cur.week);
    if (weekStart < sd) {
        cur = nextYearWeek(cur);
    }
    while (!isYearWeekAfter(cur, endYW)) {
        result.push(Object.assign({}, cur));
        cur = nextYearWeek(cur);
    }
    return result;
}
export function buildMonthOptions(startDate, endDate) {
    const result = [];
    const sd = new Date(startDate);
    const ed = new Date(endDate);
    let y = sd.getFullYear();
    let m = sd.getMonth() + 1;
    if (sd.getDate() > 1) {
        m++;
        if (m > 12) {
            m = 1;
            y++;
        }
    }
    while (y < ed.getFullYear() || (y === ed.getFullYear() && m <= ed.getMonth() + 1)) {
        result.push({ month: m, year: y });
        m++;
        if (m > 12) {
            m = 1;
            y++;
        }
    }
    return result;
}
export function buildYearOptions(startDate, endDate) {
    const sy = new Date(startDate).getFullYear();
    const ey = new Date(endDate).getFullYear();
    const result = [];
    for (let y = sy; y <= ey; y++)
        result.push(y);
    return result;
}
